import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  apartmentBookingApiSlice,
  useGetApartmentDetailsQuery,
} from "../../../../api/apartment/apartmentApiSlice";
import { RootState } from "../../../../redux/store";
import { setApartmentDetails } from "../../../../redux/slice/apartmentRental/apartmentRentalSlice";
import { setAssetGroup } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";

/**
 * Loads the selected room's details and mirrors them into the slices the
 * later steps read (`apartment.apartmentDetails`,
 * `bookingCreation.assetGroupId`).
 *
 * Extracted from the two ApartmentOverview pages and ALSO mounted on the
 * booking-summary step: URL hydration restores the step and the selected
 * venue after a refresh, but these two values were only ever written by the
 * overview step's effect — so a refresh on the personal-data or summary step
 * arrived with no room name (the summary showed the thumbnail's "Room" alt
 * text instead), an empty thumbnail, and a submit button whose
 * missing-group guard returned without any visible reaction.
 */
export const useEnsureApartmentDetails = () => {
  const dispatch = useDispatch();
  const selectedApartment = useSelector(
    (state: RootState) => state.apartment.selectedApartment
  );
  const assetId = selectedApartment?.assetId ?? "";

  const query = useGetApartmentDetailsQuery(
    { apartmentId: assetId },
    { skip: !assetId }
  );
  const { data } = query;

  useEffect(() => {
    if (data) {
      dispatch(setApartmentDetails(data));
      dispatch(setAssetGroup({ id: data.groupId }));
    }
  }, [data, dispatch]);

  return query;
};

/**
 * Did loading the selected object's details FAIL? Read from the same cache
 * entry the hook above fills, without subscribing to it — for the shell,
 * which must not offer "Weiter" beside an overview that could not load.
 */
export const selectObjectDetailsFailed = (state: RootState): boolean => {
  const assetId = state.apartment.selectedApartment?.assetId;
  if (!assetId) return false;
  return apartmentBookingApiSlice.endpoints.getApartmentDetails.select({
    apartmentId: assetId,
  })(state).isError;
};
